import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Table, Button, Input, Space, Empty } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import { EyeOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";

function ClientValves() {
  const [valves, setValves] = useState([]);
  const [valvesFilter, setValvesFilter] = useState([]);
  const [loading, setLoading] = useState(true);

  function handleSearch(search, key) {
    const searchTerm = search?.toLowerCase() || "";
    const data = valves.filter((item) => {
      const value = item[key];
      return value?.toString().toLowerCase().includes(searchTerm);
    });
    setValvesFilter(search ? data : valves);
  }

  function reset() {
    setValvesFilter(valves);
  }

  const columns = [
    {
      title: "TAG",
      dataIndex: "tag",
      key: "tag",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por TAG"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "tag");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#e6be01" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Nº Série",
      dataIndex: "numeroSerie",
      key: "numeroSerie",
      filterDropdown: ({ setSelectedKeys, selectedKeys, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Filtrar por Nº Série"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            style={{ marginBottom: 8, width: 250 }}
          />
          <Space style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="primary"
              onClick={() => {
                handleSearch(selectedKeys[0], "numeroSerie");
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
            >
              Filtrar
            </Button>
            <Button
              onClick={() => {
                setSelectedKeys([]);
                reset();
                close();
              }}
              size="small"
            >
              Limpar
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? "#e6be01" : undefined, fontSize: 16 }}
        />
      ),
    },
    {
      title: "Empresa",
      dataIndex: "nomeEmpresa",
      key: "nomeEmpresa",
    },
    {
      title: "Unidade",
      dataIndex: "unidadeCliente",
      key: "unidadeCliente",
    },
    {
      title: "Tipo de Válvula",
      dataIndex: "tipoValvula",
      key: "tipoValvula",
    },
    {
      title: "SPL",
      dataIndex: "id",
      key: "spl",
    },
    {
      title: "Válvula",
      dataIndex: "id",
      key: "actions",
      render: (id, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Link
            to={`/cliente/cadastro-valvula/${record.tipoColecao}/${id}`}
            className="view-btn"
          >
            <EyeOutlined />
          </Link>
        </div>
      ),
    },
  ];

  const fetchClientsFirebase = async () => {
    const snapshot = await getDocs(collection(db, "clientes"));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const fetchFromCollection = async (collectionName, tipoColecao) => {
    const snapshot = await getDocs(collection(db, collectionName));
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      tipoColecao,
      ...doc.data(),
    }));
  };

  const fetchValvesFirebase = async () => {
    try {
      setLoading(true);
      const seepilUsuario = JSON.parse(sessionStorage.getItem("seepilUsuario"));

      const clienteId = seepilUsuario?.cliente_id;

      const unidadesPermitidas =
        Array.isArray(seepilUsuario?.unidadesCliente) &&
        seepilUsuario.unidadesCliente.length > 0
          ? seepilUsuario.unidadesCliente
          : seepilUsuario?.unidadeCliente
          ? [seepilUsuario.unidadeCliente]
          : [];

      if (!clienteId || unidadesPermitidas.length === 0) {
        setValves([]);
        setValvesFilter([]);
        setLoading(false);
        return;
      }

      const [psv, controle, bloqueio] = await Promise.all([
        fetchFromCollection("valvulasPSV", "PSV"),
        fetchFromCollection("valvulasControle", "Controle"),
        fetchFromCollection("valvulasBloqueio", "Bloqueio"),
      ]);

      const allValves = [...psv, ...controle, ...bloqueio];

      const filteredByClientAndUnit = allValves.filter(
        (item) =>
          item.cliente_id === clienteId &&
          unidadesPermitidas.includes(item.unidadeCliente)
      );

      const clients = await fetchClientsFirebase();

      const valvesWithClients = filteredByClientAndUnit.map((valve) => {
        const client = clients.find((c) => c.id === valve.cliente_id);
        return {
          ...valve,
          nomeEmpresa: client?.nomeEmpresa || "Desconhecida",
        };
      });

      const orderedValves = valvesWithClients.sort((a, b) => {
        const dateA = a.dataCadastro?.toDate?.() || new Date(0);
        const dateB = b.dataCadastro?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setValves(orderedValves);
      setValvesFilter(orderedValves);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar válvulas:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValvesFirebase();
  }, []);

  return (
    <Container userType="cliente">
      <div className="valve-list-container">
        <div className="list-card">
          <div className="list-header">
            <div className="list-header-left">
              <div className="list-icon">
                <SettingOutlined />
              </div>
              <div>
                <h2 className="list-title">Válvulas Cadastradas</h2>
                <p className="list-subtitle">{valvesFilter.length} válvulas encontradas</p>
              </div>
            </div>
          </div>

          <Table
            dataSource={valvesFilter}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
            className="custom-table"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Nenhuma válvula encontrada"
                />
              ),
            }}
          />
        </div>
      </div>
    </Container>
  );
}

export default ClientValves;
